import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import ToggleGroup, { ToggleOption } from 'components/shared/form/ToggleGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import Upload from 'hooks/useDirectUpload/Upload';
import { simpleDashDate } from 'lib/date';
import Group from 'models/Group';
import GroupType from 'models/GroupType';
import { ReviewStatus, ReviewType } from 'models/Review';
import { GroupAccessMode, useInfiniteGroups } from 'services/api/group';
import { CreateCoordinateRequestInput, CreateCoordinateRequestResponse, createReview } from 'services/api/reviews';

type CoordinateRequestModalProps = {
  patientName: string;
  importedPatientId: string;
  onCancel: () => void;
  onAutoAccepted: (episodeId: string) => void;
  onRequestPending: () => void;
  onError: () => void;
};

export default function CoordinateRequestModal(props: CoordinateRequestModalProps) {
  const { patientName, importedPatientId, onCancel, onAutoAccepted, onRequestPending, onError } = props;
  const { profile } = useProfile();

  const groupTypeOptions: ToggleOption[] = useMemo(
    () =>
      profile.enabledProviderTypes.map((gt: GroupType) => ({
        label: gt.displayName || gt.name,
        value: gt.id,
      })),
    [profile.enabledProviderTypes]
  );

  const showGroupTypeToggle = groupTypeOptions.length > 1;

  const [selectedGroupType, setSelectedGroupType] = useState<ToggleOption>(
    groupTypeOptions[0] ?? { label: '', value: '' }
  );
  const [admissionDate, setAdmissionDate] = useState<Date | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const selectedGroupTypeName = useMemo(() => {
    const match = profile.enabledProviderTypes.find((gt: GroupType) => gt.id === selectedGroupType.value);
    return match?.displayName || match?.name || '';
  }, [profile.enabledProviderTypes, selectedGroupType.value]);

  const asyncGroupOptions = useInfiniteGroups({
    groupType: selectedGroupType.value || undefined,
    accessModes: [GroupAccessMode.Allowed],
    sortBy: 'name asc',
  });

  const { note, setNote } = useActivityInput();
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  const { mutate, isPending } = useMutation<CreateCoordinateRequestResponse, Error, CreateCoordinateRequestInput>({
    mutationFn: createReview,
    onSuccess: (data) => {
      if (data.status === ReviewStatus.ACCEPTED && data.locationEpisode?.episodeId) {
        onAutoAccepted(data.locationEpisode.episodeId);
      } else {
        onRequestPending();
      }
    },
    onError,
  });

  useEffect(() => {
    setSelectedGroup(null);
  }, [selectedGroupType.value]);

  const handleGroupTypeChange = (option: ToggleOption) => {
    setSelectedGroupType(option);
  };

  const handleSubmit = () => {
    if (!selectedGroupType.value || !selectedGroup) return;

    const noteText = note.text || note.plaintext;
    const noteAttachments = note.attachments;
    const hasNote = !!noteText || noteAttachments.length > 0;

    mutate({
      type: ReviewType.COORDINATE_REQUEST,
      include: 'location_episode',
      importedPatientId,
      providerGroupId: selectedGroup.id,
      admissionDate: simpleDashDate(admissionDate) ?? undefined,
      note: hasNote
        ? {
            text: note.text,
            plaintext: note.plaintext,
            mentions: note.mentions,
            attachments: noteAttachments
              .filter((a): a is Upload => a instanceof Upload)
              .map((upload) => upload.serialize()),
          }
        : undefined,
    });
  };

  const submitDisabled = !selectedGroupType.value || !selectedGroup || isPending || isUploadingAttachments;

  return (
    <StandardModal title='Coordinate' onCancel={onCancel}>
      <PatientName>Patient: {patientName}</PatientName>

      <FormContainer>
        <HalfWidth>
          <InputGroup title='Actual Admission Date (optional)'>
            <DatePicker selected={admissionDate} maxDate={new Date()} onChange={setAdmissionDate} $fullWidth />
          </InputGroup>
        </HalfWidth>

        {showGroupTypeToggle && (
          <InputGroup title='What care is the patient receiving?'>
            <ToggleGroup
              options={groupTypeOptions}
              selectedOption={selectedGroupType}
              disabled={false}
              onChange={handleGroupTypeChange}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
          </InputGroup>
        )}

        <InputGroup title={selectedGroupTypeName}>
          <Select<Group>
            {...asyncGroupOptions}
            placeholder={`Select ${selectedGroupTypeName || 'a group'}`}
            value={selectedGroup}
            onChange={setSelectedGroup}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            disabled={!selectedGroupType.value}
          />
        </InputGroup>

        <FieldLabel>Additional Notes (optional)</FieldLabel>
        <ActivityInput taggable={false} values={note} setValues={setNote} onUploading={setIsUploadingAttachments} />
      </FormContainer>

      <Actions>
        <Button variant='ghost' type='button' onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitDisabled} loading={isPending}>
          Coordinate
        </Button>
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const HalfWidth = styled.div`
  width: 50%;
`;
